import { useState, useRef, useEffect } from 'react';
import { Category, Post, PostLanguage } from '@/types/post';
import { usePosts } from '@/contexts/PostsContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { processFilesToOptimizedBlobs } from '@/utils/fileProcessor';
import { supabase } from '@/lib/supabase';

const CATEGORIES: Category[] = [
  'Global Affairs',
  'International Order',
  'Rights and Freedoms',
  'Politics',
  'Justice and Freedom',
  'Law and Society',
  'Uncategorized',
];

const LANG_OPTIONS: { value: PostLanguage; label: string }[] = [
  { value: 'ko', label: '한국어' },
  { value: 'en', label: 'English' },
  { value: 'both', label: 'Both' },
];

interface Props {
  onClose: () => void;
  editPost?: Post | null;
}

interface LocalSlide {
  id: string;
  file: Blob;
  previewUrl: string;
  name: string;
}

export default function UploadModal({ onClose, editPost }: Props) {
  const { addPost, updatePost } = usePosts();
  const { lang } = useLanguage();
  const fileRef = useRef<HTMLInputElement>(null);

  const isEdit = !!editPost;

  const [slides, setSlides] = useState<LocalSlide[]>(
    isEdit
      ? editPost!.slides.map((url) => ({
          id: crypto.randomUUID(),
          file: new Blob(),
          previewUrl: url,
          name: 'existing',
        }))
      : []
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const [titleEn, setTitleEn] = useState(isEdit ? editPost!.title.en : '');
  const [titleKo, setTitleKo] = useState(isEdit ? editPost!.title.ko : '');
  const [bodyEn, setBodyEn] = useState(isEdit ? editPost!.body.en : '');
  const [bodyKo, setBodyKo] = useState(isEdit ? editPost!.body.ko : '');
  const [category, setCategory] = useState<Category>(isEdit ? editPost!.category as Category : 'Uncategorized');
  const [language, setLanguage] = useState<PostLanguage>(isEdit ? editPost!.language : 'both');
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Drag-and-drop reorder state
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dropTargetIdx, setDropTargetIdx] = useState<number | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  /* ========== 파일 처리 핵심 함수 ========== */

  async function handleFiles(files: File[]) {
    console.log('handleFiles called', files);
    setError('');
    setLoading(true);

    try {
      for (const file of files) {
        console.log('processing file', file.name, file.type, file.size);
      }
      const processed = await processFilesToOptimizedBlobs(files);

      if (!processed.length) {
        throw new Error('처리된 이미지가 없습니다.');
      }

      setSlides((prev) => [...prev, ...processed]);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : '파일 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }

  async function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    console.log('selected files', files);
    if (!files.length) return;

    await handleFiles(files);

    // 같은 파일 다시 올릴 수 있게 초기화
    e.target.value = '';
  }

  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    await handleFiles(files);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  /* ========== Storage 업로드 (직접 — Edge Function 거치지 않음) ========== */

  async function uploadSlidesToStorage(slidesToUpload: { id: string; file: Blob; name: string }[]) {
    console.log('[Storage] uploading', slidesToUpload.length, 'slides');
    const urls: string[] = [];

    for (const slide of slidesToUpload) {
      const filePath = `posts/${Date.now()}-${slide.id}.jpg`;
      console.log('[Storage] uploading:', slide.name, 'size:', slide.file.size, 'path:', filePath);

      const { error: uploadError } = await supabase.storage
        .from('slide-images')
        .upload(filePath, slide.file, {
          contentType: 'image/jpeg',
          upsert: false,
        });

      if (uploadError) {
        console.error('[Storage] upload error:', uploadError);
        throw new Error(`Storage 업로드 실패 [${slide.name}]: ${uploadError.message}`);
      }

      const { data } = supabase.storage.from('slide-images').getPublicUrl(filePath);
      console.log('[Storage] uploaded URL:', data.publicUrl);
      urls.push(data.publicUrl);
    }

    return urls;
  }

  /* ========== 기존 UI 핸들러 ========== */

  const remove = (idx: number) => {
    setSlides((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      setActiveIdx(Math.min(activeIdx, Math.max(0, next.length - 1)));
      return next;
    });
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= slides.length) return;
    setSlides((prev) => {
      const next = [...prev];
      const [s] = next.splice(from, 1);
      next.splice(to, 0, s);
      return next;
    });
    setActiveIdx(to);
  };

  /* Drag-and-drop reorder handlers */
  const handleDragStart = (idx: number) => {
    setDraggedIdx(idx);
  };

  const handleDragOverItem = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== idx) {
      setDropTargetIdx(idx);
    }
  };

  const handleDragLeave = () => {
    setDropTargetIdx(null);
  };

  const handleDropItem = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== idx) {
      setSlides((prev) => {
        const next = [...prev];
        const [s] = next.splice(draggedIdx, 1);
        next.splice(idx, 0, s);
        return next;
      });
      setActiveIdx(idx);
    }
    setDraggedIdx(null);
    setDropTargetIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDropTargetIdx(null);
  };

  const submit = async () => {
    if (!slides.length) { setError('이미지를 1장 이상 추가하세요.'); return; }

    setLoading(true);

    try {
      // 기존 이미지는 그대로 URL 사용, 새 이미지만 Storage 업로드
      const existingUrls = slides.filter((s) => s.name === 'existing').map((s) => s.previewUrl);
      const newSlides = slides.filter((s) => s.name !== 'existing');

      let urls = [...existingUrls];
      if (newSlides.length > 0) {
        const uploaded = await uploadSlidesToStorage(newSlides);
        urls = [...existingUrls, ...uploaded];
      }

      const data = { slides: urls, titleEn, titleKo, bodyEn, bodyKo, category, language };
      if (isEdit) await updatePost(editPost!.id, data);
      else await addPost(data);

      setDone(true);
      setTimeout(() => onClose(), 800);
    } catch (err: any) {
      setError(err?.message || '저장 실패');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-stone-100 flex items-center justify-between">
          <h2 className="font-bold text-stone-900">{isEdit ? '게시물 수정' : '새 게시물'}</h2>
          <button onClick={() => { if (!loading) onClose(); }} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
            <i className="ri-close-line text-stone-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Preview */}
          <div className="relative w-full aspect-[4/5] max-h-[360px] rounded-xl overflow-hidden bg-stone-100 flex items-center justify-center">
            {slides.length > 0 ? (
              <img src={slides[activeIdx]?.previewUrl} alt="preview" className="w-full h-full object-contain" />
            ) : (
              <div className="text-center text-stone-400">
                <i className="ri-image-line text-4xl mb-2" />
                <p className="text-sm">이미지를 추가하세요</p>
              </div>
            )}
            {slides.length > 1 && (
              <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">{activeIdx + 1} / {slides.length}</div>
            )}
          </div>

          {/* Thumbnails */}
          {slides.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {slides.map((s, idx) => (
                <div
                  key={s.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOverItem(e, idx)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDropItem(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`relative shrink-0 group cursor-grab active:cursor-grabbing ${
                    draggedIdx === idx ? 'opacity-40' : 'opacity-100'
                  } ${dropTargetIdx === idx ? 'scale-110' : ''} transition-transform`}
                >
                  <button
                    onClick={() => setActiveIdx(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer ${activeIdx === idx ? 'border-stone-900' : 'border-transparent'}`}
                  >
                    <img src={s.previewUrl} alt="" className="w-full h-full object-cover" />
                  </button>
                  {/* Order number badge */}
                  <div className="absolute top-0.5 left-0.5 bg-black/60 text-white text-[10px] px-1 py-0.5 rounded leading-none font-medium">
                    {idx + 1}
                  </div>
                  <button
                    onClick={() => remove(idx)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs cursor-pointer opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-close-line" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-0.5 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => move(idx, idx - 1)}
                      disabled={idx === 0}
                      className="w-5 h-5 bg-black/50 text-white rounded text-xs cursor-pointer disabled:opacity-30"
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    <button
                      onClick={() => move(idx, idx + 1)}
                      disabled={idx === slides.length - 1}
                      className="w-5 h-5 bg-black/50 text-white rounded text-xs cursor-pointer disabled:opacity-30"
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Drag hint */}
          {slides.length > 1 && (
            <p className="text-[11px] text-stone-400 text-center -mt-2">썸네일을 드래그해서 순서를 바꿀 수 있어요</p>
          )}

          {/* Drop zone */}
          <div
            onDragOver={(e) => { handleDragOver(e); if (!loading) setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => !loading && fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${dragging ? 'border-stone-500 bg-stone-50' : 'border-stone-200 hover:border-stone-400'} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? (
              <div className="flex flex-col items-center gap-1">
                <i className="ri-loader-4-line text-2xl text-stone-400 animate-spin" />
                <p className="text-sm text-stone-500 font-medium">{error || '처리 중...'}</p>
              </div>
            ) : (
              <>
                <i className="ri-image-add-line text-2xl text-stone-400 mb-1" />
                <p className="text-sm text-stone-500 font-medium">클릭 또는 드래그로 추가</p>
                <p className="text-xs text-stone-400 mt-1">이미지, PDF, ZIP</p>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*,.heic,.heif,.pdf,.zip" multiple className="hidden" onChange={handleInputChange} />
          </div>

          {/* Language */}
          <div className="flex gap-2">
            {LANG_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => setLanguage(opt.value)} className={`text-xs px-3 py-1.5 rounded-full border font-semibold cursor-pointer whitespace-nowrap ${language === opt.value ? 'bg-stone-900 text-white border-stone-900' : 'border-stone-200 text-stone-500'}`}>
                {opt.label}
              </button>
            ))}
          </div>

          <input type="text" placeholder="제목 (영어)" value={titleEn} onChange={e => setTitleEn(e.target.value)} className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:outline-none focus:border-stone-400" />
          <input type="text" placeholder="제목 (한국어)" value={titleKo} onChange={e => setTitleKo(e.target.value)} className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:outline-none focus:border-stone-400" />
          <textarea placeholder="본문 (영어)" value={bodyEn} onChange={e => setBodyEn(e.target.value)} rows={3} className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50 resize-none focus:outline-none focus:border-stone-400" />
          <textarea placeholder="본문 (한국어)" value={bodyKo} onChange={e => setBodyKo(e.target.value)} rows={3} className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50 resize-none focus:outline-none focus:border-stone-400" />
          <select value={category} onChange={e => setCategory(e.target.value as Category)} className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50 cursor-pointer">
            {CATEGORIES.map(c => <option key={c} value={c}>{c === 'Uncategorized' ? '카테고리 없음' : c}</option>)}
          </select>
        </div>

        {/* Footer */}
        {error && !done && (
          <div className="px-5 py-2 bg-red-50 border-t border-red-100 text-xs text-red-600">{error}</div>
        )}
        <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between">
          <span className="text-xs text-stone-400">{slides.length}장</span>
          <button onClick={submit} disabled={!slides.length || loading || done} className={`text-sm px-6 py-2 rounded-full font-semibold cursor-pointer whitespace-nowrap ${done ? 'bg-teal-600 text-white' : !slides.length ? 'bg-stone-200 text-stone-400 cursor-not-allowed' : 'bg-stone-900 text-white'}`}>
            {done ? '완료!' : loading ? (error || '처리 중...') : isEdit ? '수정하기' : '게시하기'}
          </button>
        </div>
      </div>
    </div>
  );
}